import { useState } from 'react';
import { Document } from '../components/document-list-vault/types';
import { NWClient } from '../../../client/NWClient';
import { DocumentSigningModel } from '../../../models/document.signing.model';
import { ROUTES } from '../../../common/constants/routes';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SIGNING_DATA } from '../types';
import { showMessage } from '../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { hideOverlay } from '../../../slices/show-progress-overlay/showProgressOverlay';
import { selectBusinessProfileDetails } from '../../../slices/business-profile/businessProfileSlice';
import { sanitizeRedirectUrl } from '../../../common/helpers/sanitize';
import { useDocumensoRedirect } from '../../../common/hooks/useDocumensoRedirect';
import { fetchDocuments, selectDocumentsPage } from '../../../slices/documents/documentsSlice';
import { DocumentVaultSearchData } from '../../../models/document.vault.item.model';
import { useErrorMessage } from '../../../common/hooks/useErrorMessage';

type SigningData = {
  user: number;
  category?: string;
  document_upload_html?: number | null;
  document_contract?: number;
  document_upload?: number | null;
  profile_id?: number;
};

export const useDocumentSigning = (cardView?: boolean) => {
  const currentUser = useAppSelector(selectUser);
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const { documentId } = useParams();
  const { customerId } = useParams();
  const { supplierId } = useParams();
  const { employeeId } = useParams();
  const documentsPage = useAppSelector(selectDocumentsPage);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const businessProfileDetails = useAppSelector(selectBusinessProfileDetails);
  const path = location.pathname;
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [document, setDocument] = useState<Document>(null as unknown as Document);
  const [openAssignPersonDialog, setOpenAssignPersonDialog] = useState<boolean>(false);

  const { redirectToDocumenso } = useDocumensoRedirect();

  const getProfileLabelsProp = (category: string) => {
    if (category === 'employment') {
      return 'employees';
    } else if (category === 'business') {
      return 'business_profile_contacts';
    } else {
      return category;
    }
  };

  const handleSign = (
    data: SIGNING_DATA,
    doc: Document,
    fetchCurrentDocumentsPage?: boolean,
    tab?: Window
  ) => {
    setPendingStatus(true);
    setDialogOpen(false);
    NWClient.post('document-sign', data, true)
      .then((response) => {
        const res = response as DocumentSigningModel;
        redirectToDocumenso(
          `${ROUTES.DOCUMENTS}/${res.signing_doc_id}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}${
            path === ROUTES.DOCUMENT_VAULT ? `&activeTab=${doc?.category}` : ''
          }`,
          tab
        );
        if (documentId) {
          window.location.reload();
        } else {
          const requestData = () => {
            const dataRequest: DocumentVaultSearchData = {
              data: {
                user: currentUser?.id,
                category: doc?.category,
                page: fetchCurrentDocumentsPage ? documentsPage : 1,
              },
            };
            if (cardView) {
              if (
                supplierId ||
                customerId ||
                employeeId ||
                location.pathname.includes(ROUTES.BUSINESS_PROFILE)
              ) {
                if (
                  doc &&
                  'du_workflows' in doc &&
                  Array.isArray(doc.du_workflows) &&
                  doc.du_workflows.length > 0
                ) {
                  dataRequest.data.workflow = doc.du_workflows[0];
                } else if (
                  doc &&
                  'dc_workflows' in doc &&
                  Array.isArray(doc.dc_workflows) &&
                  doc.dc_workflows.length > 0
                ) {
                  dataRequest.data.workflow = doc.dc_workflows[0];
                } else {
                  dataRequest.data.workflow = t('labels.generalAdministration');
                }
              }
            }

            if (supplierId || customerId || employeeId) {
              dataRequest.data.profile =
                Number(supplierId) || Number(customerId) || Number(employeeId);
            }

            return dataRequest;
          };
          dispatch(fetchDocuments({ ...requestData() }));
        }

        //navigate(document_sign_url(String(res.signing_doc_id)));
      })
      .catch((error) => {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.userNotAssociatedWithCompany')
        ) {
          navigate(
            `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
          );
        } else {
          toast.error(getErrorMessage(error));
        }
        if (tab) {
          tab.location.href = `${window.location.origin}${ROUTES.REGULAR_ERROR}`;
        }
      })
      .finally(() => {
        setPendingStatus(false);
        dispatch(hideOverlay());
      });
  };

  const handleSignLatestVersion = (
    doc: Document,
    latestHTMLId?: number,
    profileId?: number,
    fetchCurrentDocumentsPage?: boolean,
    tab?: Window
  ) => {
    const data: SigningData = {
      user: currentUser.id,
      document_upload: 'document_upload_html' in doc ? doc.id : null,
      document_upload_html:
        'document_upload_html' in doc
          ? latestHTMLId
            ? latestHTMLId
            : Math.max(...(doc.document_upload_html ?? []))
          : null,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    if (doc.category === 'suppliers' || doc.category === 'business') {
      data.profile_id = profileId;
      data.category = doc.category;
    } else if (
      doc &&
      (((doc.category === 'employment' || doc.category === 'customers') &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        doc[getProfileLabelsProp(doc.category)].length > 0) ||
        doc.category === 'business')
    ) {
      data.profile_id =
        doc.category === 'business'
          ? businessProfileDetails?.id
          : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            doc[getProfileLabelsProp(doc.category)][0];
      data.category = doc.category;
    }
    handleSign(data, doc, fetchCurrentDocumentsPage, tab);
  };

  const handleSignOriginalDocument = (
    doc: Document,
    profileId?: number,
    fetchCurrentDocumentsPage?: boolean,
    tab?: Window
  ) => {
    const data: SigningData = {
      user: currentUser.id,
      ['contract_generation' in doc ? 'document_contract' : 'document_upload']: doc.id,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    if (doc.category === 'suppliers' || doc.category === 'business') {
      data.profile_id = profileId;
      data.category = doc.category;
    } else if (
      doc &&
      (((doc.category === 'employment' || doc.category === 'customers') &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        doc[getProfileLabelsProp(doc.category)].length > 0) ||
        doc.category === 'business')
    ) {
      data.profile_id =
        doc.category === 'business'
          ? businessProfileDetails?.id
          : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            doc[getProfileLabelsProp(doc.category)][0];
      data.category = doc.category;
    }
    handleSign(data, doc, fetchCurrentDocumentsPage, tab);
  };

  return {
    dialogOpen,
    setDialogOpen,
    document,
    setDocument,
    pendingStatus,
    setPendingStatus,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  };
};
